import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaTable, FaCheckCircle, FaInfoCircle, FaDatabase, FaCogs, FaMagic } from 'react-icons/fa';
import { isHubModule } from '../../utils/moduli';

/**
 * Heuristica di singolarizzazione per nomi italiani.
 * Convenzione MILO/HUB: il nome tabella è al SINGOLARE (es. "Azienda", "Paziente").
 * Regole semplici (l'utente può comunque modificare manualmente):
 *   - parole che terminano in "he" -> "a"  (Amiche -> Amica)
 *   - parole che terminano in "ci"/"gi" -> "o"/"o" (Medici -> Medico)
 *   - parole che terminano in "e"  -> "a"  (Aziende -> Azienda)
 *   - parole che terminano in "i"  -> "e"  (Clienti -> Cliente)
 *   - altrimenti restituisce la parola invariata.
 */
const suggestSingularItalian = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (trimmed.length < 3) return trimmed;

  const lower = trimmed.toLowerCase();
  const lastChar = lower.slice(-1);
  const last2 = lower.slice(-2);

  let singularLower = lower;

  if (last2 === 'he') {
    singularLower = lower.slice(0, -2) + 'a';                 // Amiche -> Amica
  } else if (last2 === 'ci' || last2 === 'gi') {
    singularLower = lower.slice(0, -1) + 'o';                 // Medici -> Medico
  } else if (lastChar === 'e') {
    singularLower = lower.slice(0, -1) + 'a';                 // Aziende -> Azienda
  } else if (lastChar === 'i') {
    singularLower = lower.slice(0, -1) + 'e';                 // Clienti -> Cliente
  } else {
    return trimmed; // niente da fare (già singolare o non riconosciuto)
  }

  // Conserva la capitalizzazione iniziale
  if (trimmed[0] === trimmed[0].toUpperCase()) {
    return singularLower.charAt(0).toUpperCase() + singularLower.slice(1);
  }
  return singularLower;
};

const CreateTableConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  macroFunzioneData,
  moduleData,
  themeColor = 'emerald'
}) => {
  const isHub = isHubModule({
    moduleCode: moduleData?.original?.MOD_CodiceModulo || moduleData?.prefix,
    moduleId: moduleData?.value || moduleData?.MOD_Id_Moduli,
  });
  const macNome = macroFunzioneData?.MAC_Nome || '';
  const suggestedSingular = useMemo(
    () => (isHub ? suggestSingularItalian(macNome) : macNome),
    [isHub, macNome]
  );

  // Nome tabella effettivo: editabile solo se HUB; default = suggerimento singolare.
  const [tableName, setTableName] = useState(suggestedSingular || macNome);

  // Re-sincronizza quando cambia la macroFunzione o il modulo (es. riapertura modale).
  useEffect(() => {
    setTableName(isHub ? (suggestedSingular || macNome) : macNome);
  }, [isHub, suggestedSingular, macNome]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    const finalName = (tableName || macNome).trim();
    // Passa il nome scelto al chiamante (retro-compatibile: il chiamante può ignorarlo).
    onConfirm(finalName);
  };

  const isSuggestionApplied =
    isHub && suggestedSingular && tableName === suggestedSingular && suggestedSingular !== macNome;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 bg-gradient-to-r from-${themeColor}-500 to-${themeColor}-600 text-white flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <FaTable className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Creazione Tabella Automatica</h2>
                <p className="text-sm opacity-90">Configurazione per "{macNome}"</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollabile */}
        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Messaggio principale */}
          <div className="flex items-start space-x-3">
            <div className={`p-2 bg-${themeColor}-100 dark:bg-${themeColor}-900 rounded-lg flex-shrink-0`}>
              <FaInfoCircle className={`h-5 w-5 text-${themeColor}-600 dark:text-${themeColor}-400`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Nessuna tabella associata
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Non è presente una tabella associata per questa macro funzione.
                Vuoi creare automaticamente una nuova tabella SKE_Tabelle con le seguenti caratteristiche?
              </p>
            </div>
          </div>

          {/* Banner HUB: suggerimento nome al singolare */}
          {isHub && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
              <div className="flex items-start space-x-2 mb-3">
                <FaMagic className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Convenzione MILO/HUB:</strong> il nome della tabella sarà al plurale mentre l'item sarà al{' '}
                  <strong>singolare</strong> (es. <em>Azienda</em>, <em>Paziente</em>, <em>Referto</em>).
                  {suggestedSingular && suggestedSingular !== macNome && (
                    <> Suggerimento generato da "{macNome}": <strong>{suggestedSingular}</strong>.</>
                  )}
                </div>
              </div>

              <label
                htmlFor="hub-table-name"
                className="block text-xs font-semibold text-amber-900 dark:text-amber-100 mb-1 uppercase tracking-wide"
              >
                Nome tabella (singolare)
              </label>
              <div className="flex items-stretch space-x-2">
                <input
                  id="hub-table-name"
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  placeholder="Es. Azienda"
                  maxLength={100}
                />
                {suggestedSingular && tableName !== suggestedSingular && (
                  <button
                    type="button"
                    onClick={() => setTableName(suggestedSingular)}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-700 transition-colors"
                    title={`Applica suggerimento: ${suggestedSingular}`}
                  >
                    Usa "{suggestedSingular}"
                  </button>
                )}
              </div>
              {isSuggestionApplied && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                  Suggerimento applicato. Puoi comunque modificarlo a piacere.
                </p>
              )}
            </div>
          )}

          {/* Dettagli della tabella da creare */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
              <FaDatabase className="mr-2 h-4 w-4" />
              Caratteristiche della nuova tabella
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Nome:</strong> {tableName || macNome}
                    {isHub && tableName && tableName !== macNome && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        (originale: {macNome})
                      </span>
                    )}
                  </span>
                </div>

                <div className="flex items-start space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500 flex-shrink-0 mt-0.5`} />
                  <span className="text-sm text-gray-700 dark:text-white break-words">
                    <strong>Descrizione:</strong> {macroFunzioneData?.MAC_Descrizione}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Modulo:</strong> {moduleData?.label || 'N/A'}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Prefisso:</strong> {moduleData?.prefix || 'TAB'}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Acronimo Campi:</strong> {moduleData?.acronym || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Motore:</strong> MySQL (ID: 2)
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Cronologia:</strong> Abilitata
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Dati Inserimento/Modifica:</strong> Abilitati
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <FaCheckCircle className={`h-4 w-4 text-${themeColor}-500`} />
                  <span className="text-sm text-gray-700 dark:text-white">
                    <strong>Cache:</strong> Disabilitata
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Nota informativa */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <FaCogs className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Nota:</strong> Una volta creata, la tabella sarà automaticamente associata a questa macro funzione
                e potrai gestire i suoi campi tramite l'interfaccia di modifica.
              </div>
            </div>
          </div>
        </div>

        {/* Footer con pulsanti - Sempre visibile */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex-shrink-0">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors duration-200"
            >
              Annulla
            </button>
            <button
              onClick={handleConfirm}
              disabled={!tableName || !tableName.trim()}
              className={`px-4 py-2 text-sm font-medium text-white bg-${themeColor}-600 hover:bg-${themeColor}-700 rounded-lg transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <FaTable className="h-4 w-4" />
              <span>Crea Tabella</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTableConfirmModal;
